import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, Users, Truck, TrendingUp, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [popularProducts, setPopularProducts] = useState([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, popularRes, oosRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary`),
        axios.get(`${API}/admin/analytics/popular-products?limit=5`),
        axios.get(`${API}/admin/products/out-of-stock`)
      ]);
      setStats(statsRes.data);
      setPopularProducts(popularRes.data);
      setOutOfStockProducts(oosRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRestockProduct = async (productId) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/toggle-stock?in_stock=true`);
      toast.success('Product marked as in stock');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    { label: 'Total Revenue', value: `R${stats?.total_revenue?.toFixed(2) || 0}`, icon: TrendingUp, color: 'text-green-600 bg-green-100' },
    { label: 'Total Orders', value: stats?.total_orders || 0, icon: ShoppingBag, color: 'text-blue-600 bg-blue-100' },
    { label: 'Pending Orders', value: stats?.pending_orders || 0, icon: AlertTriangle, color: 'text-yellow-600 bg-yellow-100' },
    { label: 'Total Products', value: stats?.total_products || 0, icon: Package, color: 'text-purple-600 bg-purple-100' },
    { label: 'Out of Stock', value: stats?.out_of_stock || 0, icon: XCircle, color: 'text-red-600 bg-red-100' },
    { label: 'Active Drivers', value: stats?.total_drivers || 0, icon: Truck, color: 'text-orange-600 bg-orange-100' },
    { label: 'Customers', value: stats?.total_customers || 0, icon: Users, color: 'text-teal-600 bg-teal-100' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card key={stat.label} className="border-stone-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone-500">{stat.label}</p>
                    <p className="text-2xl font-bold text-stone-900" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="border-stone-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Top Selling Products</CardTitle>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </CardHeader>
            <CardContent>
              {popularProducts.length === 0 ? (
                <p className="text-stone-500 text-center py-4">No sales data yet. Make some test purchases!</p>
              ) : (
                <div className="space-y-3">
                  {popularProducts.map((product, idx) => (
                    <div key={product.product_id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0" data-testid={`top-seller-${idx}`}>
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full bg-green-100 text-green-800 text-xs font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium text-stone-900 text-sm">{product.name}</p>
                          <p className="text-xs text-stone-500">{product.order_count} orders</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-700 text-sm">R{product.total_revenue.toFixed(2)}</p>
                        <p className="text-xs text-stone-500">{product.total_quantity} sold</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-stone-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Out of Stock Items</CardTitle>
              <Link to="/admin/products">
                <Button variant="ghost" size="sm" className="text-sm text-green-700">View All</Button>
              </Link>
            </CardHeader>
            <CardContent>
              {outOfStockProducts.length === 0 ? (
                <div className="text-center py-4">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-stone-600 font-medium">All products in stock</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {outOfStockProducts.slice(0, 5).map((product) => (
                    <div key={product.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0" data-testid={`oos-product-${product.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded bg-stone-100 overflow-hidden flex-shrink-0">
                          {product.image_url ? (
                            <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs">N/A</div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-stone-900 text-sm">{product.name}</p>
                          <span className="text-xs text-red-600 font-medium">Out of Stock</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-700 border-green-200 hover:bg-green-50 text-xs"
                        onClick={() => handleRestockProduct(product.id)}
                        data-testid={`restock-btn-${product.id}`}
                      >
                        Mark In Stock
                      </Button>
                    </div>
                  ))}
                  {outOfStockProducts.length > 5 && (
                    <p className="text-xs text-stone-500 text-center pt-2">
                      + {outOfStockProducts.length - 5} more out of stock
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-stone-200 bg-yellow-50">
            <CardContent className="p-6">
              <h3 className="font-semibold text-yellow-800 mb-2">Suspension Account</h3>
              <p className="text-sm text-yellow-700">
                Customer payments are held until orders are fulfilled.
                Mark items as out of stock to trigger automatic refunds.
              </p>
            </CardContent>
          </Card>
          <Card className="border-stone-200 bg-blue-50">
            <CardContent className="p-6">
              <h3 className="font-semibold text-blue-800 mb-2">10% Markup Applied</h3>
              <p className="text-sm text-blue-700">
                All products are automatically marked up 10% from cost price.
                This is reflected in the selling price shown to customers.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
